import { createContext, FunctionComponent, PropsWithChildren, useContext, useMemo, useState } from 'react';
import { ColumnDefinition } from './SortedTableHeader';
import { getDefaultTableColumnSettings, TableColumnProperties } from './tableSettings';
import { loadTableSettings, saveTableSettings } from './persistTableSettings';

type TableColumnSettingsContextData<TableColumnEnum extends string> = {
    columnDefinition: ColumnDefinition<TableColumnEnum>[];
    tableColumnSettings: TableColumnProperties<TableColumnEnum>;
    setTableColumnSettings: (newSettings: TableColumnProperties<TableColumnEnum>) => void;
};

const noColumns: TableColumnSettingsContextData<string> = {
    columnDefinition: [],
    tableColumnSettings: [],
    setTableColumnSettings: () => {
        console.warn('missing TableColumnSettingsContext');
    },
};

const TableColumnSettingsContext = createContext<TableColumnSettingsContextData<string>>(noColumns);

type TableColumnSettingsStateProps<TableColumnEnum extends string> = PropsWithChildren<{
    tableId: string;
    columnDefinition: ColumnDefinition<TableColumnEnum>[];
}>;

export const TableColumnSettingsState: FunctionComponent<TableColumnSettingsStateProps<string>> = ({
    tableId,
    columnDefinition,
    children,
}) => {
    const [tableColumnSettings, setTableColumnSettings] = useState<TableColumnProperties<string>>(() =>
        loadTableSettings(tableId, getDefaultTableColumnSettings(columnDefinition)),
    );

    const contextValue: TableColumnSettingsContextData<string> = useMemo(
        () => ({
            columnDefinition,
            tableColumnSettings,
            setTableColumnSettings: (newSettings: TableColumnProperties<string>) => {
                setTableColumnSettings(newSettings);
                if (tableId) {
                    saveTableSettings(tableId, newSettings);
                }
            },
        }),
        [tableId, columnDefinition, tableColumnSettings, setTableColumnSettings],
    );

    return <TableColumnSettingsContext.Provider value={contextValue}>{children}</TableColumnSettingsContext.Provider>;
};

export const useTableColumnSettings = <TableColumnEnum extends string>() =>
    useContext(TableColumnSettingsContext) as unknown as TableColumnSettingsContextData<TableColumnEnum>;
